import screenshot6 from "@/assets/screenshot6.png";
import { Alert } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageProps } from "@/types/PageProps";
import { Head, Link, router } from "@inertiajs/react";
import { CheckIcon, XIcon } from "lucide-react";
import { SubmitHandler, useForm } from "react-hook-form";
import Logo from "../assets/fulllogo_transparent.png";

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  referral: string;
};

type Props = PageProps & {
  error: string;
  wait_list_signup_success: boolean;
};

export default function SignUp({ error, notice, wait_list_signup_success }: Props) {
  const { register, handleSubmit } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    router.post("/sign_up/wait_list", data);
  };

  return (
    <div className="bg-gradient-to-b from-background to-gray-300 h-full">
      <Head title="Wait List" />
      <div className="container max-w-7xl mx-auto pt-6">
        <div className="flex justify-between items-center">
          <img src={Logo} className="h-12 mb-8" />

          <Link href="/session/new" className={buttonVariants({ variant: "ghost" })}>
            Sign in
          </Link>
        </div>

        <div className="pt-10 max-w-5xl mx-auto">
          <div className="max-w-lg mx-auto">
            <Card className="mx-auto w-full shadow-lg">
              {wait_list_signup_success ? (
                <CardHeader>
                  <CardTitle className="text-2xl">Thanks!</CardTitle>
                  <CardDescription>We'll notify you when Teeba is ready.</CardDescription>
                </CardHeader>
              ) : (
                <>
                  <CardHeader>
                    {error && (
                      <Alert variant="destructive" className="mb-8">
                        <div className="flex items-center gap-2">
                          <XIcon className="h-4 w-4" />
                          <p className="text-sm text-destructive">{error}</p>
                        </div>
                      </Alert>
                    )}
                    {notice && (
                      <Alert className="mb-8">
                        <div className="flex items-center gap-2">
                          <CheckIcon className="h-4 w-4" />
                          <p className="text-sm">{notice}</p>
                        </div>
                      </Alert>
                    )}
                    <CardTitle className="text-2xl">Join the Wait List</CardTitle>
                    <p className="pt-2">
                      Teeba is almost ready! Please join the waitlist and we'll notify you when you
                      can sign up.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form action="/session" method="post" onSubmit={handleSubmit(onSubmit)}>
                      <div className="space-y-4">
                        <div className="flex space-x-4">
                          <div className="space-y-2 flex-grow">
                            <Label htmlFor="first_name">First Name</Label>
                            <Input
                              id="first_name"
                              type="text"
                              required
                              {...register("first_name")}
                            />
                          </div>

                          <div className="space-y-2 flex-grow">
                            <Label htmlFor="last_name">Last Name</Label>
                            <Input id="last_name" type="text" required {...register("last_name")} />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" type="email" required {...register("email")} />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="referral">How did you hear about Teeba?</Label>
                          <Textarea id="referral" rows={3} {...register("referral")} />
                        </div>

                        <Button type="submit" className="w-full">
                          Notify me
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>

      <div className="">
        <div className="container max-w-7xl mx-auto py-24">
          <div className="relative">
            <div className="absolute -inset-[var(--padding)] rounded-[calc(var(--radius)+var(--padding))] shadow-sm ring-1 ring-black/5 [--padding:theme(spacing.2)]"></div>
            <div className="overflow-hidden rounded-xl shadow-2xl sm:w-auto border border-border">
              <img src={screenshot6} alt="Sign up background" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
